'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScreenshotLightbox, ImageTile, extractScreenshotImages } from '../ProcessedEntry/DomainEntry/imageComponents'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, compactDateNumeric } from '../ProcessedEntry/DomainEntry/processedTypes'
import { CHIP_TONE_CLASS, VANTAGE_LABEL, VANTAGE_TITLE, VANTAGE_TONE } from './autoDomainTypes'
import type { ScreenshotCapture } from './screenshotCaptures'

// The screenshots of a domain's browser captures -- a candidate's passes or
// an entry's entry_browser_data rows (see screenshotCaptures.ts for the one
// row shape both map to). The latest capture that stored screenshots fills
// the two tiles; every capture sits in a strip of thumbnails below, and
// clicking one brings it up into the tiles -- both scenarios, its alive/down
// line, its findings. A capture that rendered nothing is in the strip too,
// as a blank with its reason, so "the latest probe got nothing" is visible
// without hiding the last real capture.
//
// Loading is on demand: the shown capture is fetched at once (one
// Turnstile-gated /api/screenshots/bykey round trip, serialised through
// getToken), the newest `autoLoadThumbs` thumbnails fetch themselves once
// they scroll into view, and every older one only when clicked. An entry
// with dozens of captures used to fire a full both-device download for
// every thumbnail in sight; now it costs one request to open.
type Shots = { desktop: string | null; mobile: string | null }
type ShotState = 'loading' | 'error' | 'no-data' | Shots
const shotsOf = (st: ShotState | undefined): Shots | null => (st && typeof st === 'object' ? st : null)

export function noShotsWhy(c: ScreenshotCapture): string | null {
  if (c.r2Key) return null
  return c.hasScreenshots
    ? 'The session captured screenshots but they were not written to R2 — check the bucket credentials on the worker'
    : 'The browser session produced no screenshots at all; nothing was there to store. A session that returns in a second usually means the browser itself is missing or failed to start on the worker.'
}

const aliveWord = (v: boolean | null | undefined) => (v ? 'alive' : 'down')

function CaptureThumb({ capture, state, selected, autoLoad, onVisible, onPick, theme }: {
  capture: ScreenshotCapture; state: ShotState | undefined; selected: boolean; autoLoad: boolean
  onVisible: (c: ScreenshotCapture) => void; onPick: (c: ScreenshotCapture) => void; theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const ref = useRef<HTMLButtonElement>(null)
  const label = compactDateNumeric(capture.createdAt)
  const img = shotsOf(state)
  const src = img?.desktop ?? img?.mobile ?? null
  const none = !capture.r2Key
  useEffect(() => {
    const el = ref.current
    if (!el || state || none || !autoLoad) return
    const observer = new IntersectionObserver((entries) => { if (entries[0]?.isIntersecting) onVisible(capture) }, { rootMargin: '150px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [capture, state, none, autoLoad, onVisible])
  const title = none ? `${label} — no screenshots: ${noShotsWhy(capture)}`
    : state === 'error' ? `${label} — failed to load, click to retry`
    : `${label} · desktop ${aliveWord(capture.desktopIsAlive)} · mobile ${aliveWord(capture.mobileIsAlive)}${capture.vantage ? ` · ${VANTAGE_LABEL[capture.vantage]}` : ''}${!src && !autoLoad ? ' · click to load' : ''}`
  // A thumbnail that has not loaded still tells something: its date and
  // whether the host answered, as a tint.
  const anyAlive = !!capture.desktopIsAlive || !!capture.mobileIsAlive
  const bothDown = capture.desktopIsAlive === false && capture.mobileIsAlive === false
  const tint = src || none ? '' : anyAlive ? 'bg-success/10' : bothDown ? 'bg-danger-surface' : ''
  return (
    <button ref={ref} type="button" data-pass-thumb={capture.id} data-selected={selected || undefined} title={title}
      onClick={() => { onPick(capture); if (!src && !none) onVisible(capture) }}
      className={`relative aspect-[4/5] w-16 shrink-0 overflow-hidden rounded border transition-colors ${selected ? 'border-signal' : c.line} ${c.soft} ${tint} hover:border-signal`}>
      {src ? (
        <img src={src} alt={label} className="h-full w-full object-cover object-top" />
      ) : (
        <span className={`flex h-full w-full items-center justify-center font-mono text-[9px] ${state === 'error' ? 'text-destructive' : none ? 'text-warning' : c.faint}`}>
          {none ? 'none' : state === 'loading' ? '···' : state === 'error' ? 'retry' : state === 'no-data' ? 'empty' : '↓'}
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-overlay px-0.5 py-0.5 text-center font-mono text-[7px] text-white">{label}</span>
    </button>
  )
}

export function ScreenshotCaptures({ captures, theme, getToken, autoLoadThumbs = 8, noun = 'capture', reloadKey = 0, onShownLoaded }: {
  /** Newest first. */
  captures: ScreenshotCapture[]
  theme: ProcessedTheme
  getToken: () => Promise<string>
  /** How many of the newest strip thumbnails fetch themselves when scrolled
   * into view; the rest load only when clicked. 0 = click only. */
  autoLoadThumbs?: number
  /** Strip heading: "Passes · N" for a candidate, "Captures · N" for an entry. */
  noun?: 'pass' | 'capture'
  /** Bump to drop every cached image and fetch the shown capture again. */
  reloadKey?: number
  /** The shown capture's images, once loaded (the CTT report form takes them). */
  onShownLoaded?: (desktop: string | null, mobile: string | null) => void
}) {
  const c = watchClasses(theme)
  // the tiles show the newest capture that stored screenshots, or the newest
  // at all when none did
  const latestStored = useMemo(() => captures.find((r) => r.r2Key) ?? null, [captures])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const shown = captures.find((r) => r.id === selectedId) ?? latestStored ?? captures[0] ?? null
  const [shots, setShots] = useState<Record<string, ShotState>>({})
  const inFlight = useRef<Set<string>>(new Set())
  const [lightbox, setLightbox] = useState<{ src: string; label: string; description?: string | null } | null>(null)

  const load = useCallback((row: ScreenshotCapture) => {
    if (!row.r2Key) { setShots((prev) => ({ ...prev, [row.id]: 'no-data' })); return }
    if (inFlight.current.has(row.id)) return
    inFlight.current.add(row.id)
    setShots((prev) => ({ ...prev, [row.id]: 'loading' }))
    ;(async () => {
      try {
        const token = await getToken()
        const res = await fetch('/api/screenshots/bykey', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ r2Key: row.r2Key, turnstileToken: token }),
        })
        const result = await res.json()
        const got = result.success ? extractScreenshotImages(result.data) : null
        if (!got?.desktop && !got?.mobile) {
          inFlight.current.delete(row.id)
          setShots((prev) => ({ ...prev, [row.id]: result.success ? 'no-data' : 'error' }))
          return
        }
        setShots((prev) => ({ ...prev, [row.id]: { desktop: got.desktop, mobile: got.mobile } }))
      } catch {
        inFlight.current.delete(row.id)
        setShots((prev) => ({ ...prev, [row.id]: 'error' }))
      }
    })()
  }, [getToken])

  // Reload: forget every image (the selection stays) so the shown capture
  // is fetched again by the effect below.
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    inFlight.current.clear()
    setShots({})
  }, [reloadKey])

  // the capture in the tiles loads at once; the strip loads as it scrolls
  useEffect(() => { if (shown && !shots[shown.id]) load(shown) }, [shown, shots, load])

  const shownShots = shown ? shotsOf(shots[shown.id]) : null
  useEffect(() => {
    if (shownShots) onShownLoaded?.(shownShots.desktop, shownShots.mobile)
  }, [shownShots, onShownLoaded])

  if (!shown) {
    return (
      <div className={`rounded-md border px-4 py-8 text-center font-mono text-[11px] uppercase tracking-[0.24em] ${c.line} ${c.soft} ${c.faint}`}>
        No browser images
      </div>
    )
  }
  const state = shots[shown.id]
  const img = shownShots
  const retry = () => { inFlight.current.delete(shown.id); load(shown) }
  const fallback = state === 'loading' || state === undefined ? 'Loading' : state === 'error' ? <button onClick={retry} className="text-destructive">Retry</button> : 'No image'
  const newest = captures[0]
  const newerThanShown = newest && newest.id !== shown.id && newest.createdAt > shown.createdAt
  const findings = shown.findings ?? []
  const nounPlural = noun === 'pass' ? 'Passes' : 'Captures'
  return (
    <div data-browser-row={shown.id}>
      {lightbox && <ScreenshotLightbox src={lightbox.src} label={lightbox.label} description={lightbox.description} onClose={() => setLightbox(null)} />}
      {newerThanShown && (
        <div className={`mb-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`} title={noShotsWhy(newest) ?? 'A later capture exists; click its thumbnail to bring it up'}>
          Latest with screenshots · {compactDate(shown.createdAt)} · later probe {compactDate(newest.createdAt)} {newest.r2Key ? '' : 'rendered none'}
        </div>
      )}
      <div className={`mb-2 flex flex-wrap items-center gap-2 font-mono text-[10px] ${c.faint}`}>
        <span>{compactDate(shown.createdAt)}</span>
        {shown.vantage && <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS[VANTAGE_TONE[shown.vantage]]}`} title={VANTAGE_TITLE[shown.vantage]}>{VANTAGE_LABEL[shown.vantage]}</span>}
        <span className={shown.desktopIsAlive ? 'text-success' : 'text-destructive'}>desktop {aliveWord(shown.desktopIsAlive)}</span>
        <span className={shown.mobileIsAlive ? 'text-success' : 'text-destructive'}>mobile {aliveWord(shown.mobileIsAlive)}</span>
        {shown.mobileEngine && <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS.neutral}`} title="The mobile scenario's browser: the real iPhone 17 Safari, or the iPhone User-Agent on desktop Chromium">mobile: {shown.mobileEngine}</span>}
        {shown.proxyLabel && <span>via {shown.proxyLabel}</span>}
        {shown.statusCode != null && <span>HTTP {shown.statusCode}</span>}
        {shown.parked && <span className="text-warning">parked</span>}
        {shown.blocked && <span className="text-warning">blocked</span>}
        {shown.finalUrl && <span className="max-w-[320px] truncate" title={shown.finalUrl}>→ {shown.finalUrl}</span>}
        {!shown.r2Key && <span className="text-warning" title={noShotsWhy(shown) ?? undefined}>{shown.hasScreenshots ? 'captured, not stored' : 'no screenshots captured'}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ImageTile label="Mobile" src={img?.mobile ?? null} caption={compactDateNumeric(shown.createdAt)} description={shown.descriptions?.mobile} fallback={fallback}
          onExpand={() => img?.mobile && setLightbox({ src: img.mobile, label: `Mobile · ${compactDate(shown.createdAt)}`, description: shown.descriptions?.mobile })} />
        <ImageTile label="Desktop" src={img?.desktop ?? null} caption={compactDateNumeric(shown.createdAt)} description={shown.descriptions?.desktop} fallback={fallback}
          onExpand={() => img?.desktop && setLightbox({ src: img.desktop, label: `Desktop · ${compactDate(shown.createdAt)}`, description: shown.descriptions?.desktop })} />
      </div>
      {findings.length > 0 && (
        <ul className={`mt-2 list-disc pl-5 text-[11px] leading-relaxed ${c.muted}`}>
          {findings.slice(0, 8).map((f, i) => <li key={i}>{typeof f === 'string' ? f : f.description ?? f.type ?? JSON.stringify(f)}</li>)}
        </ul>
      )}
      {captures.length > 1 && (
        <div className="mt-3">
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
            {nounPlural} · {captures.length} · {captures.filter((x) => x.r2Key).length} with screenshots
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {captures.map((row, i) => (
              <CaptureThumb key={row.id} capture={row} state={shots[row.id]} selected={row.id === shown.id} autoLoad={i < autoLoadThumbs}
                onVisible={load} onPick={(x) => setSelectedId(x.id)} theme={theme} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
